/**
 * Autonomous Agent Panel
 * P0 - Site Scanner-driven autonomous prospecting
 *
 * Embedded inside ProspectingAgent.jsx as the primary tab.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sparkles,
  Settings,
  Play,
  StopCircle,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Calendar,
  Target,
  Zap,
  Globe,
  ChevronDown,
  Send,
  Activity,
  Mail,
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { GlassCard, PillButton } from '../components/ios26/IOSComponents';

const COMMON_TIMEZONES = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Berlin',
  'Europe/Rome',
  'America/New_York',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'UTC',
];

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function StatPill({ icon: Icon, label, value, color = 'hsl(210, 100%, 50%)' }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl"
      style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: color + '20', color }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-semantic-muted">{label}</div>
        <div className="text-base font-semibold text-semantic truncate">{value}</div>
      </div>
    </div>
  );
}

function DiagItem({ label, ok, hint, neutral = false }) {
  const color = neutral
    ? 'hsl(220, 10%, 50%)'
    : ok
    ? 'hsl(142, 70%, 45%)'
    : 'hsl(0, 70%, 50%)';
  const Icon = neutral ? Activity : ok ? CheckCircle : XCircle;
  return (
    <div
      className="flex items-start gap-2 p-2.5 rounded-lg"
      style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
    >
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color }} />
      <div className="min-w-0">
        <div className="text-xs text-semantic-muted">{label}</div>
        <div className="text-sm font-medium text-semantic truncate">
          {hint != null ? hint : (ok ? 'oui' : 'non')}
        </div>
      </div>
    </div>
  );
}

export default function AutonomousAgentPanel() {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState(null);
  const [scannedProjects, setScannedProjects] = useState([]);
  const [generatedQueries, setGeneratedQueries] = useState([]);
  const [history, setHistory] = useState([]);
  const [progress, setProgress] = useState(null);  // P0: daily target progress
  const [brevoDiag, setBrevoDiag] = useState(null);  // P0 iter 119
  const [metrics, setMetrics] = useState(null);  // P0 iter 124
  const [serperEfficiency, setSerperEfficiency] = useState(null);  // P0 iter 134
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkSyncResult, setBulkSyncResult] = useState(null);
  const [bulkSyncConfirm, setBulkSyncConfirm] = useState(false);

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // P0: poll history+progress while a background run is active
  const [activeRunId, setActiveRunId] = useState(null);
  // P0 iter 127: multi-source metrics + manual run trigger
  const [sourcesMetrics, setSourcesMetrics] = useState(null);
  const [sourcesRunning, setSourcesRunning] = useState(false);
  const [sourcesRunResult, setSourcesRunResult] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, projectsRes, queriesRes, historyRes, progressRes, brevoDiagRes, metricsRes, sourcesRes, efficiencyRes] = await Promise.all([
        apiClient.get('/prospecting-agent/autonomous/settings'),
        apiClient.get('/prospecting-agent/autonomous/scanned-projects'),
        apiClient.get('/prospecting-agent/autonomous/generated-queries'),
        apiClient.get('/prospecting-agent/autonomous/history?limit=10'),
        apiClient.get('/prospecting-agent/autonomous/progress'),
        apiClient.get('/prospecting-agent/brevo-diagnostics'),
        apiClient.get('/prospecting-agent/autonomous/metrics'),
        apiClient.get('/prospecting-agent/lead-sources/metrics?days=7').catch(() => ({ data: null })),
        apiClient.get('/prospecting-agent/autonomous/serper-efficiency').catch(() => ({ data: null })),
      ]);
      setSettings(settingsRes.data);
      setScannedProjects(projectsRes.data?.projects || []);
      setGeneratedQueries(queriesRes.data?.queries || []);
      setHistory(historyRes.data?.runs || []);
      setProgress(progressRes.data);
      setBrevoDiag(brevoDiagRes.data);
      setMetrics(metricsRes.data);
      setSourcesMetrics(sourcesRes.data || null);
      setSerperEfficiency(efficiencyRes.data || null);
    } catch (err) {
      console.error('autonomous load error', err);
      setError("Impossible de charger les paramètres de l'agent autonome.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Light polling when a run is active (max 4 minutes)
  useEffect(() => {
    if (!activeRunId) return;
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      try {
        const [historyRes, progressRes, metricsRes] = await Promise.all([
          apiClient.get('/prospecting-agent/autonomous/history?limit=10'),
          apiClient.get('/prospecting-agent/autonomous/progress'),
          apiClient.get('/prospecting-agent/autonomous/metrics'),
        ]);
        setHistory(historyRes.data?.runs || []);
        setProgress(progressRes.data);
        setMetrics(metricsRes.data);

        // Stop polling if the latest run is finished or 4 min elapsed
        const latest = (historyRes.data?.runs || [])[0];
        const finished = latest && ['completed', 'completed_with_errors', 'failed'].includes(latest.status);
        if (finished || Date.now() - startedAt > 4 * 60 * 1000) {
          clearInterval(interval);
          setActiveRunId(null);
          setRunning(false);
        }
      } catch {
        // ignore polling errors
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [activeRunId]);

  const patchSettings = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient.patch('/prospecting-agent/autonomous/settings', patch);
      setSettings(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!settings?.project_id) {
      setError('Sélectionnez un projet source avant de lancer un run.');
      return;
    }
    setRunning(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await apiClient.post('/prospecting-agent/autonomous/run-now');
      // P0: backend now returns immediately with {queued:true, run_id}
      if (res.data?.queued) {
        setSuccessMessage(
          `Run lancé en arrière-plan. L'agent va boucler jusqu'à ${settings.daily_target_qualified_contacts} contacts qualifiés ou ${settings.max_cycles_per_run} cycles.`
        );
        setActiveRunId(res.data.run_id);
        await loadAll();
      } else {
        // legacy synchronous response
        const stats = res.data?.stats || {};
        setSuccessMessage(
          `Run terminé (${res.data?.status}) — ${stats.queries_executed || 0} requêtes, ` +
            `${stats.new_prospects || 0} nouveaux, ` +
            `${stats.qualified || 0} qualifiés, ` +
            `${stats.synced_to_brevo || 0} sync Brevo, ` +
            `${stats.errors || 0} erreurs.`,
        );
        await loadAll();
        setRunning(false);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de l'exécution du run.");
      setRunning(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      await apiClient.post('/prospecting-agent/autonomous/stop');
      setSuccessMessage("Demande d'arrêt envoyée. Le run en cours s'arrêtera aux prochaines actions.");
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de l'arrêt.");
    } finally {
      setStopping(false);
    }
  };

  // P0 iter 119: bulk sync all qualified contacts to Brevo
  const handleBulkSync = async (forceLive = false) => {
    setBulkSyncing(true);
    setBulkSyncResult(null);
    setError(null);
    try {
      const res = await apiClient.post('/prospecting-agent/bulk-sync-brevo', {
        confirm: true,
        force_live: forceLive,
      });
      setBulkSyncResult(res.data);
      await loadAll();
    } catch (err) {
      setBulkSyncResult({
        success: false,
        results: {},
        message: err?.response?.data?.detail || 'Erreur de bulk sync',
      });
    } finally {
      setBulkSyncing(false);
      setBulkSyncConfirm(false);
    }
  };

  // P0 iter 129: lead-sources job polling (async)
  const [leadJob, setLeadJob] = useState(null);
  const leadJobPollRef = useRef(null);

  // P0 iter 127/129: trigger a multi-source run (async job)
  const handleRunMultiSources = async () => {
    setSourcesRunning(true);
    setSourcesRunResult(null);
    try {
      // Use the latest generated queries as seeds; pick first 1
      const seedQueries = (generatedQueries || []).slice(0, 1).map((q) => q.query || q);
      const fallback = ['agence voyage Paris contact'];
      const queries = seedQueries.length > 0 ? seedQueries : fallback;
      const res = await apiClient.post('/prospecting-agent/lead-sources/run', {
        queries,
        num_results: 4,
        include_partners: true,
      }, { timeout: 10000 });

      const data = res.data || {};
      if (data.queued && data.job_id) {
        setLeadJob({ job_id: data.job_id, status: 'queued' });
        setSourcesRunResult({ success: true, queued: true, job_id: data.job_id });
        // Start polling
        if (leadJobPollRef.current) clearInterval(leadJobPollRef.current);
        leadJobPollRef.current = setInterval(async () => {
          try {
            const jr = await apiClient.get(`/prospecting-agent/lead-sources/jobs/${data.job_id}`);
            setLeadJob(jr.data);
            const terminal = ['completed', 'completed_with_errors', 'failed'].includes(jr.data?.status);
            if (terminal) {
              clearInterval(leadJobPollRef.current);
              leadJobPollRef.current = null;
              setSourcesRunning(false);
              await loadAll();
            }
          } catch {
            // keep polling until terminal or user navigates away
          }
        }, 3000);
      } else {
        setSourcesRunResult({ success: false, message: data.message || "Le job n'a pas pu être démarré." });
        setSourcesRunning(false);
      }
    } catch (err) {
      setSourcesRunResult({
        success: false,
        message: err?.response?.data?.detail || 'Erreur multi-sources',
      });
      setSourcesRunning(false);
    }
  };

  // Load the latest job on mount so we resume polling across refreshes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get('/prospecting-agent/lead-sources/jobs/latest');
        if (cancelled) return;
        const data = r.data || {};
        if (data.job_id) {
          setLeadJob(data);
          const running = ['queued', 'running'].includes(data.status);
          if (running) {
            setSourcesRunning(true);
            if (leadJobPollRef.current) clearInterval(leadJobPollRef.current);
            leadJobPollRef.current = setInterval(async () => {
              try {
                const jr = await apiClient.get(`/prospecting-agent/lead-sources/jobs/${data.job_id}`);
                setLeadJob(jr.data);
                const terminal = ['completed', 'completed_with_errors', 'failed'].includes(jr.data?.status);
                if (terminal) {
                  clearInterval(leadJobPollRef.current);
                  leadJobPollRef.current = null;
                  setSourcesRunning(false);
                  await loadAll();
                }
              } catch {}
            }, 3000);
          }
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
      if (leadJobPollRef.current) clearInterval(leadJobPollRef.current);
    };
  }, []);

  // P0 iter 122: manual refresh of the Brevo diagnostics card (users can
  // re-trigger the check after saving new Brevo credentials).
  const [refreshingDiag, setRefreshingDiag] = useState(false);
  const handleRefreshBrevoDiag = async () => {
    setRefreshingDiag(true);
    try {
      const res = await apiClient.get('/prospecting-agent/brevo-diagnostics');
      setBrevoDiag(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur de diagnostic Brevo.');
    } finally {
      setRefreshingDiag(false);
    }
  };

  // P0 iter 123: daily report (Resend, not Brevo)
  const [sendingReportTest, setSendingReportTest] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const handleSendReportTest = async () => {
    setSendingReportTest(true);
    setReportResult(null);
    try {
      const res = await apiClient.post('/prospecting-agent/autonomous/report-test', {});
      setReportResult(res.data);
      // Refresh settings to get new last_report_sent_at
      const s = await apiClient.get('/prospecting-agent/autonomous/settings');
      setSettings(s.data);
    } catch (err) {
      setReportResult({
        success: false,
        error: 'request_failed',
        message: err?.response?.data?.detail || "Erreur lors de l'envoi.",
      });
    } finally {
      setSendingReportTest(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-semantic-muted" />
      </div>
    );
  }

  const hasProjects = scannedProjects.length > 0;
  const lastRun = history[0] || null;

  return (
    <div className="space-y-6" data-testid="autonomous-panel">
      {/* Messages */}
      {error && (
        <div
          className="p-4 rounded-xl flex items-center gap-3"
          style={{ backgroundColor: 'hsl(0, 100%, 95%)', color: 'hsl(0, 70%, 40%)' }}
          data-testid="autonomous-error"
        >
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}
      {successMessage && (
        <div
          className="p-4 rounded-xl flex items-center gap-3"
          style={{ backgroundColor: 'hsl(142, 70%, 95%)', color: 'hsl(142, 70%, 30%)' }}
          data-testid="autonomous-success"
        >
          <CheckCircle className="w-5 h-5" />
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="ml-auto">×</button>
        </div>
      )}

      {/* === P0: Daily target progress === */}
      {progress && (
        <GlassCard className="p-6" data-testid="autonomous-progress-card">
          <div className="flex items-start justify-between mb-3 gap-4">
            <div>
              <h2 className="text-lg font-semibold text-semantic">
                Objectif quotidien
              </h2>
              <p className="text-sm text-semantic-muted">
                Contacts <strong>qualifiés</strong> avec email professionnel valide.
                Les prospects incomplets ne consomment <em>pas</em> cet objectif.
              </p>
            </div>
            <div
              className="px-4 py-2 rounded-xl text-sm font-bold"
              style={{
                backgroundColor: progress.target_reached ? 'hsl(142, 70%, 92%)' : 'hsl(40, 100%, 92%)',
                color: progress.target_reached ? 'hsl(142, 70%, 30%)' : 'hsl(40, 100%, 30%)',
              }}
              data-testid="autonomous-progress-status"
            >
              {progress.target_reached
                ? `Objectif atteint — ${progress.qualified_today}/${progress.target}`
                : `Objectif non atteint — ${progress.qualified_today}/${progress.target} contacts qualifiés`}
            </div>
          </div>

          {/* Progress bar */}
          <div
            className="w-full h-3 rounded-full overflow-hidden"
            style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
            data-testid="autonomous-progress-bar"
          >
            <div
              className="h-full transition-all"
              style={{
                width: `${progress.progress_pct || 0}%`,
                backgroundColor: progress.target_reached ? 'hsl(142, 70%, 45%)' : 'hsl(210, 100%, 50%)',
              }}
            />
          </div>

          {/* Sub-stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <StatPill
              icon={Sparkles}
              label="Contacts qualifiés"
              value={`${progress.qualified_today}`}
              color="hsl(142, 70%, 45%)"
            />
            <StatPill
              icon={Activity}
              label="Prospects bruts (24h)"
              value={`${progress.raw_today} / ${progress.raw_cap}`}
              color="hsl(220, 60%, 50%)"
            />
            <StatPill
              icon={Send}
              label="Synchronisés Brevo (24h)"
              value={`${metrics?.synced_today ?? 0}`}
              color="hsl(270, 70%, 50%)"
            />
            <StatPill
              icon={Activity}
              label="Cycles dernier run"
              value={`${progress.latest_run?.stats?.cycles_executed || 0} / ${settings?.max_cycles_per_run || 5}`}
              color="hsl(40, 100%, 45%)"
            />
          </div>

          {activeRunId && (
            <div
              className="mt-4 p-3 rounded-xl flex items-center gap-3 text-sm"
              style={{ backgroundColor: 'hsl(210, 100%, 95%)', color: 'hsl(210, 100%, 30%)' }}
              data-testid="autonomous-active-run-banner"
            >
              <RefreshCw className="w-4 h-4 animate-spin" />
              Run en cours (id <code>{activeRunId.slice(-6)}</code>) — la page se met à jour automatiquement.
            </div>
          )}
        </GlassCard>
      )}

      {/* === P0 iter 124: Massive exploration metrics === */}
      {metrics && (
        <GlassCard className="p-6" data-testid="exploration-metrics-card">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-semantic flex items-center gap-2">
                <Zap className="w-5 h-5" style={{ color: 'hsl(32, 100%, 50%)' }} />
                Exploration intensive
              </h2>
              <p className="text-sm text-semantic-muted">
                L'agent teste des <strong>centaines de requêtes B2B</strong> par jour
                jusqu'à atteindre l'objectif.
              </p>
            </div>
            <div
              className="px-4 py-2 rounded-xl text-sm font-bold"
              data-testid="exploration-status-badge"
              style={{
                backgroundColor: {
                  target_reached: 'hsl(142, 70%, 92%)',
                  exploring: 'hsl(210, 100%, 92%)',
                  quota_reached: 'hsl(0, 70%, 92%)',
                  serper_cap_reached: 'hsl(40, 100%, 92%)',
                  serper_cap_exhausted: 'hsl(220, 10%, 92%)',
                  stopped: 'hsl(0, 70%, 92%)',
                  idle: 'hsl(220, 10%, 92%)',
                }[metrics.status] || 'hsl(220, 10%, 92%)',
                color: {
                  target_reached: 'hsl(142, 70%, 30%)',
                  exploring: 'hsl(210, 100%, 30%)',
                  quota_reached: 'hsl(0, 70%, 30%)',
                  serper_cap_reached: 'hsl(40, 100%, 30%)',
                  serper_cap_exhausted: 'hsl(220, 10%, 40%)',
                  stopped: 'hsl(0, 70%, 30%)',
                  idle: 'hsl(220, 10%, 40%)',
                }[metrics.status] || 'hsl(220, 10%, 40%)',
              }}
            >
              {{
                target_reached: '✓ Objectif atteint',
                exploring: '⟳ Exploration en cours',
                quota_reached: '⚠ Quota API atteint',
                serper_cap_reached: '⚠ Quota Serper — enrichissement local en cours',
                serper_cap_exhausted: '✓ Quota Serper atteint — aucun prospect local exploitable',
                stopped: '■ Arrêté',
                idle: '— Inactif',
              }[metrics.status] || metrics.status}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatPill
              icon={Sparkles}
              label="Requêtes générées (pool)"
              value={`${metrics.queries_generated_today}`}
              color="hsl(32, 100%, 50%)"
            />
            <StatPill
              icon={Activity}
              label="Requêtes testées 24h"
              value={`${metrics.queries_tested_today} / ${metrics.caps.queries_cap}`}
              color="hsl(210, 70%, 50%)"
            />
            <StatPill
              icon={Target}
              label="Taux de qualification"
              value={`${metrics.qualification_rate_pct}%`}
              color="hsl(142, 70%, 45%)"
            />
            <StatPill
              icon={Send}
              label="Sync Brevo 24h"
              value={`${metrics.synced_today}`}
              color="hsl(270, 70%, 50%)"
            />
          </div>

          {/* Cap bars */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-semantic-muted">Cap Serper (requêtes / jour)</span>
                <span className="font-semibold text-semantic">
                  {metrics.queries_tested_today} / {metrics.caps.queries_cap}
                </span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden"
                   style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}>
                <div className="h-full transition-all"
                     style={{
                       width: `${metrics.caps.queries_used_pct}%`,
                       backgroundColor: metrics.caps.queries_used_pct > 85 ? 'hsl(0, 70%, 50%)' : 'hsl(210, 70%, 50%)',
                     }}
                     data-testid="cap-queries-bar"/>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-semantic-muted">Cap prospects bruts (24h)</span>
                <span className="font-semibold text-semantic">
                  {metrics.raw_today} / {metrics.caps.raw_cap}
                </span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden"
                   style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}>
                <div className="h-full transition-all"
                     style={{
                       width: `${metrics.caps.raw_used_pct}%`,
                       backgroundColor: metrics.caps.raw_used_pct > 85 ? 'hsl(0, 70%, 50%)' : 'hsl(32, 100%, 50%)',
                     }}
                     data-testid="cap-raw-bar"/>
              </div>
            </div>
          </div>

          {/* Top patterns used today */}
          {metrics.top_patterns_today && metrics.top_patterns_today.length > 0 && (
            <div className="mt-4" data-testid="top-patterns-today">
              <div className="text-xs text-semantic-muted mb-2">
                Meilleurs patterns aujourd'hui
              </div>
              <div className="flex flex-wrap gap-2">
                {metrics.top_patterns_today.map((p) => (
                  <span
                    key={p.pattern}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
                    style={{
                      backgroundColor: 'hsl(var(--color-surface-2))',
                      color: 'hsl(var(--color-text))',
                    }}
                  >
                    <span className="font-medium">{p.pattern}</span>
                    <span className="text-semantic-muted">× {p.count}</span>
                    {p.raw_results > 0 && (
                      <span className="text-green-600 font-semibold">
                        → {p.raw_results}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Pool pattern breakdown */}
          {metrics.pool_top_patterns && metrics.pool_top_patterns.length > 0 && (
            <div className="mt-3 text-xs text-semantic-muted">
              Pool généré :{' '}
              {metrics.pool_top_patterns.map((p, i) => (
                <span key={p.pattern}>
                  {i > 0 && ' · '}
                  <span className="font-medium">{p.pattern}</span> ({p.count})
                </span>
              ))}
            </div>
          )}
        </GlassCard>
      )}

      {/* === P0 iter 134: Rentabilité Serper === */}
      {serperEfficiency && (
        <GlassCard className="p-6" data-testid="serper-efficiency-card">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-semantic flex items-center gap-2">
                <Zap className="w-5 h-5" style={{ color: 'hsl(32, 100%, 50%)' }} />
                Rentabilité Serper
              </h2>
              <p className="text-sm text-semantic-muted">
                Coût en requêtes Serper par contact qualifié. Cible : &lt; 10 requêtes/contact, idéalement 3-5.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatPill
              icon={Activity}
              label="Crédits consommés"
              value={`${serperEfficiency.queries_used_today}`}
              color="hsl(210, 70%, 50%)"
            />
            <StatPill
              icon={CheckCircle}
              label="Contacts qualifiés"
              value={`${serperEfficiency.qualified_today}`}
              color="hsl(142, 70%, 45%)"
            />
            <StatPill
              icon={Target}
              label="Requêtes / qualifié"
              value={serperEfficiency.queries_per_qualified > 0 ? `${serperEfficiency.queries_per_qualified}` : '—'}
              color={
                serperEfficiency.queries_per_qualified === 0
                  ? 'hsl(220, 10%, 50%)'
                  : serperEfficiency.queries_per_qualified <= 5
                    ? 'hsl(142, 70%, 45%)'
                    : serperEfficiency.queries_per_qualified <= 10
                      ? 'hsl(40, 100%, 45%)'
                      : 'hsl(0, 70%, 50%)'
              }
            />
            <StatPill
              icon={Send}
              label="Budget soft restant"
              value={`${serperEfficiency.soft_budget_remaining} / ${serperEfficiency.effective_cap}`}
              color="hsl(270, 70%, 50%)"
            />
          </div>

          {serperEfficiency.top_patterns && serperEfficiency.top_patterns.length > 0 && (
            <div className="mb-3" data-testid="serper-top-patterns">
              <div className="text-xs text-semantic-muted mb-2 font-medium">
                ✨ Top 5 patterns rentables (7 derniers jours)
              </div>
              <div className="flex flex-wrap gap-2">
                {serperEfficiency.top_patterns.map((p) => (
                  <span
                    key={p.pattern}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
                    style={{ backgroundColor: 'hsl(142, 70%, 92%)', color: 'hsl(142, 70%, 30%)' }}
                  >
                    <span className="font-medium">{p.pattern}</span>
                    <span>· {p.qualified} qual. · {(p.email_rate * 100).toFixed(1)}% emails</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {serperEfficiency.worst_patterns && serperEfficiency.worst_patterns.length > 0 && (
            <div data-testid="serper-worst-patterns">
              <div className="text-xs text-semantic-muted mb-2 font-medium">
                ⚠ Top 5 patterns à désactiver (aucun résultat ou taux trop faible)
              </div>
              <div className="flex flex-wrap gap-2">
                {serperEfficiency.worst_patterns.map((p) => (
                  <span
                    key={p.pattern}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs line-through opacity-80"
                    style={{ backgroundColor: 'hsl(0, 70%, 92%)', color: 'hsl(0, 70%, 40%)' }}
                  >
                    <span className="font-medium">{p.pattern}</span>
                    <span>· {p.queries} req · {p.qualified} qual.</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </GlassCard>
      )}

      {/* === P0 iter 127: Multi-source lead generation === */}
      <GlassCard className="p-6" data-testid="lead-sources-card">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-semantic flex items-center gap-2">
              <Globe className="w-5 h-5" style={{ color: 'hsl(180, 60%, 45%)' }} />
              Sources de leads
            </h2>
            <p className="text-sm text-semantic-muted">
              Google Maps, Annuaires pro, LinkedIn public et Partner extractor — tous orchestrés
              en parallèle avec déduplication globale par domaine.
            </p>
          </div>
          <PillButton
            variant="primary"
            onClick={handleRunMultiSources}
            disabled={sourcesRunning}
            data-testid="run-multi-sources-btn"
          >
            {sourcesRunning ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Recherche en cours…
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Lancer un scan multi-sources
              </>
            )}
          </PillButton>
        </div>

        {sourcesRunResult && (
          <div
            className="mb-4 p-3 rounded-xl text-sm"
            style={{
              backgroundColor: sourcesRunResult.success ? 'hsl(210, 100%, 95%)' : 'hsl(0, 70%, 95%)',
              color: sourcesRunResult.success ? 'hsl(210, 100%, 30%)' : 'hsl(0, 70%, 35%)',
            }}
            data-testid="multi-sources-run-result"
          >
            {sourcesRunResult.success ? (
              <>
                ⟳ Job <code>{(sourcesRunResult.job_id || '').slice(-8)}</code> démarré en arrière-plan…
              </>
            ) : (
              <>✗ {sourcesRunResult.message || 'Erreur multi-sources'}</>
            )}
          </div>
        )}

        {leadJob && leadJob.job_id && (
          <div
            className="mb-4 p-3 rounded-xl text-sm"
            style={{
              backgroundColor:
                leadJob.status === 'completed' ? 'hsl(142, 70%, 95%)' :
                leadJob.status === 'completed_with_errors' ? 'hsl(40, 100%, 95%)' :
                leadJob.status === 'failed' ? 'hsl(0, 70%, 95%)' :
                'hsl(210, 100%, 96%)',
              color:
                leadJob.status === 'completed' ? 'hsl(142, 70%, 30%)' :
                leadJob.status === 'completed_with_errors' ? 'hsl(40, 100%, 30%)' :
                leadJob.status === 'failed' ? 'hsl(0, 70%, 35%)' :
                'hsl(210, 100%, 30%)',
            }}
            data-testid="lead-job-progress"
          >
            <div className="font-semibold flex items-center gap-2">
              {{
                queued: '⏳ En file d\'attente',
                running: '⟳ En cours',
                completed: '✓ Terminé',
                completed_with_errors: '⚠ Terminé avec erreurs',
                failed: '✗ Échec',
              }[leadJob.status] || leadJob.status}
              {leadJob.current_source && (
                <span className="text-xs font-normal opacity-75">
                  · source en cours : <strong>{leadJob.current_source}</strong>
                </span>
              )}
            </div>
            <div className="text-xs mt-1 flex flex-wrap gap-3">
              <span><strong>{leadJob.prospects_found || 0}</strong> prospects ajoutés</span>
              <span><strong>{leadJob.emails_found || 0}</strong> avec email</span>
              <span><strong>{leadJob.skipped_duplicates || 0}</strong> doublons ignorés</span>
              {leadJob.sources_done && leadJob.sources_done.length > 0 && (
                <span>
                  sources OK : {leadJob.sources_done.join(', ')}
                </span>
              )}
              {leadJob.sources_failed && leadJob.sources_failed.length > 0 && (
                <span className="text-red-700">
                  sources KO : {leadJob.sources_failed.join(', ')}
                </span>
              )}
            </div>
          </div>
        )}

        {sourcesMetrics && sourcesMetrics.sources && sourcesMetrics.sources.length > 0 ? (
          <>
            <div className="text-xs text-semantic-muted mb-2">
              Performance par source (fenêtre {sourcesMetrics.window_days || 7}j)
              {sourcesMetrics.top_source_today && (
                <>
                  {' '}· Top du jour :{' '}
                  <span className="font-semibold text-semantic">
                    {sourcesMetrics.top_source_today}
                  </span>
                </>
              )}
            </div>
            <div className="overflow-x-auto" data-testid="lead-sources-metrics-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-semantic-muted border-b">
                    <th className="text-left py-2 pr-3">Source</th>
                    <th className="text-right py-2 px-3">Leads (24h)</th>
                    <th className="text-right py-2 px-3">Total uniques</th>
                    <th className="text-right py-2 px-3">Avec email</th>
                    <th className="text-right py-2 px-3">Score moyen</th>
                    <th className="text-right py-2 px-3">Qualifiés</th>
                    <th className="text-right py-2 pl-3">Taux qualif</th>
                  </tr>
                </thead>
                <tbody>
                  {sourcesMetrics.sources.map((s) => (
                    <tr
                      key={s.source}
                      className="border-b last:border-0"
                      data-testid={`source-row-${s.source}`}
                    >
                      <td className="py-2 pr-3 font-medium">
                        {{
                          google_maps: '🗺️ Google Maps',
                          directory: '📒 Annuaires pro',
                          linkedin_public: '💼 LinkedIn public',
                          partner_extractor: '🤝 Partner extractor',
                        }[s.source] || s.source}
                      </td>
                      <td className="text-right py-2 px-3">{s.qualified_today || 0}</td>
                      <td className="text-right py-2 px-3">{s.leads_unique}</td>
                      <td className="text-right py-2 px-3">{s.with_email}</td>
                      <td className="text-right py-2 px-3">{s.avg_score}</td>
                      <td className="text-right py-2 px-3">{s.qualified_count}</td>
                      <td className="text-right py-2 pl-3">
                        <span
                          className={
                            s.qualification_rate_pct >= 30
                              ? 'text-green-600 font-semibold'
                              : s.qualification_rate_pct >= 15
                              ? 'text-orange-600'
                              : 'text-semantic-muted'
                          }
                        >
                          {s.qualification_rate_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="p-4 rounded-xl text-sm text-semantic-muted text-center"
               style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}>
            Aucune donnée multi-sources pour l'instant.
            Cliquez sur <strong>Lancer un scan multi-sources</strong> pour démarrer.
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-semantic-muted">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(180, 60%, 45%)' }} />
            Google Maps · prio 100
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(220, 60%, 50%)' }} />
            Annuaires · prio 90
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(280, 60%, 50%)' }} />
            Partner ext. · prio 85
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(210, 60%, 45%)' }} />
            LinkedIn · prio 80
          </div>
        </div>
      </GlassCard>

      {/* === P0 iter 119: Brevo diagnostics card === */}
      {brevoDiag && (
        <GlassCard className="p-6" data-testid="brevo-diagnostics-card">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-semantic flex items-center gap-2">
                <Send className="w-5 h-5" style={{ color: 'hsl(270, 70%, 50%)' }} />
                Diagnostic Brevo
              </h2>
              <p className="text-sm text-semantic-muted">
                État de la synchronisation et raison principale si rien ne part.
              </p>
            </div>

            <PillButton
              variant={brevoDiag.qualified_syncable_count > 0 ? 'primary' : 'outline'}
              onClick={() => setBulkSyncConfirm(true)}
              disabled={brevoDiag.qualified_syncable_count === 0 || bulkSyncing}
              data-testid="bulk-sync-brevo-btn"
              title={
                brevoDiag.qualified_syncable_count === 0
                  ? 'Aucun contact qualifié synchronisable'
                  : `Synchroniser les ${brevoDiag.qualified_syncable_count} contact(s) qualifié(s)`
              }
            >
              {bulkSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Synchronisation…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Sync tous les qualifiés ({brevoDiag.qualified_syncable_count})
                </>
              )}
            </PillButton>
          </div>

          {/* P0 iter 122: refresh button to re-evaluate diagnostics after config change */}
          <div className="flex items-center justify-end mb-2">
            <button
              type="button"
              onClick={handleRefreshBrevoDiag}
              disabled={refreshingDiag}
              className="inline-flex items-center gap-1.5 text-xs text-semantic-muted hover:text-semantic transition-colors disabled:opacity-60"
              data-testid="refresh-brevo-diag-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshingDiag ? 'animate-spin' : ''}`} />
              Rafraîchir le diagnostic
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <DiagItem
              label="Brevo configuré"
              ok={brevoDiag.configured}
              data-testid="diag-configured"
            />
            <DiagItem
              label="API key"
              ok={brevoDiag.api_key_present}
            />
            <DiagItem
              label="Clé valide"
              ok={brevoDiag.decryption_ok}
              hint={
                brevoDiag.decryption_ok
                  ? 'oui'
                  : brevoDiag.api_key_present
                  ? 'à re-saisir'
                  : 'absente'
              }
              data-testid="diag-decryption-ok"
            />
            <DiagItem
              label="Liste cible"
              ok={brevoDiag.list_id_configured}
              hint={
                brevoDiag.list_id
                  ? (brevoDiag.list_name
                      ? `${brevoDiag.list_name} (id ${brevoDiag.list_id})`
                      : `id ${brevoDiag.list_id}`)
                  : 'aucune'
              }
            />
            <DiagItem
              label="Mode LIVE"
              ok={brevoDiag.mode_live}
              hint={brevoDiag.mode_live ? 'live' : 'simulation'}
            />
            <DiagItem
              label="Auto-sync"
              ok={brevoDiag.auto_sync_enabled}
              hint={brevoDiag.auto_sync_enabled ? 'actif' : 'désactivé'}
            />
            <DiagItem
              label="Qualifiés avec email"
              ok={brevoDiag.qualified_with_email > 0}
              hint={`${brevoDiag.qualified_with_email}`}
            />
            <DiagItem
              label="Déjà synchronisés"
              ok={true}
              hint={`${brevoDiag.already_synced}`}
              neutral
            />
            <DiagItem
              label="Synchronisables"
              ok={brevoDiag.qualified_syncable_count > 0}
              hint={`${brevoDiag.qualified_syncable_count}`}
            />
          </div>

          {brevoDiag.first_blocking_reason && (
            <div
              className="mt-4 p-3 rounded-xl text-sm flex items-start gap-2"
              style={{ backgroundColor: 'hsl(40, 100%, 95%)', color: 'hsl(40, 100%, 30%)' }}
              data-testid="brevo-blocking-reason"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Raison du blocage :</span>{' '}
                {(() => {
                  const reasons = {
                    no_qualified_contact: 'Aucun contact qualifié pour le moment.',
                    all_synced: 'Tous les contacts qualifiés ont déjà été synchronisés.',
                    brevo_not_configured: 'Brevo n\'est pas configuré (rendez-vous dans les paramètres pour ajouter la clé API).',
                    no_target_list: 'Aucune liste cible Brevo sélectionnée. Ouvrez la configuration Brevo et choisissez une liste.',
                    api_key_undecryptable: 'La clé API Brevo est enregistrée mais ne peut pas être déchiffrée. Merci de la re-saisir dans la configuration Brevo.',
                    simulation_mode: 'Le mode simulation est actif — désactivez-le pour synchroniser réellement.',
                    auto_sync_disabled: 'L\'auto-sync Brevo est désactivé. Vous pouvez quand même utiliser le bouton ci-dessus.',
                  };
                  return reasons[brevoDiag.first_blocking_reason] || brevoDiag.first_blocking_reason;
                })()}
              </div>
            </div>
          )}

          {bulkSyncResult && (
            <div
              className="mt-4 p-3 rounded-xl text-sm"
              style={{
                backgroundColor: bulkSyncResult.success ? 'hsl(142, 70%, 95%)' : 'hsl(0, 70%, 95%)',
                color: bulkSyncResult.success ? 'hsl(142, 70%, 30%)' : 'hsl(0, 70%, 35%)',
              }}
              data-testid="bulk-sync-result"
            >
              <div className="font-semibold">
                {bulkSyncResult.success ? '✓ Bulk sync exécuté' : '✗ Échec bulk sync'}
                {' — '}
                {bulkSyncResult.candidate_count || 0} candidat(s)
                {bulkSyncResult.effective_dry_run && ' (mode simulation)'}
              </div>
              {bulkSyncResult.results && (
                <div className="text-xs flex flex-wrap gap-2 mt-1">
                  {Object.entries(bulkSyncResult.results)
                    .filter(([, n]) => n > 0)
                    .map(([k, n]) => (
                      <span key={k} className="px-2 py-0.5 rounded-full bg-white/60">
                        {k} × {n}
                      </span>
                    ))}
                </div>
              )}
              {bulkSyncResult.message && (
                <div className="text-xs mt-1">{bulkSyncResult.message}</div>
              )}
            </div>
          )}
        </GlassCard>
      )}

      {/* === P0 iter 123: Daily internal report card (Resend, not Brevo) === */}
      <GlassCard className="p-6" data-testid="daily-report-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-semantic flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: 'hsl(220, 90%, 55%)' }} />
              Rapport journalier
            </h2>
            <p className="text-sm text-semantic-muted">
              L'agent vous envoie chaque jour un récapitulatif interne (via Resend).
              Brevo n'est jamais utilisé pour ce rapport — uniquement pour la synchronisation des contacts prospects.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-semantic-muted">Activé</span>
              <input
                type="checkbox"
                checked={!!settings.daily_report_enabled}
                onChange={(e) => patchSettings({ daily_report_enabled: e.target.checked })}
                className="w-4 h-4 accent-blue-600"
                data-testid="daily-report-enabled-toggle"
              />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs text-semantic-muted mb-1">Email destinataire</label>
            <input
              type="email"
              value={settings.daily_report_email || ''}
              placeholder="Votre email (par défaut : email du compte)"
              onChange={(e) => setSettings({ ...settings, daily_report_email: e.target.value })}
              onBlur={(e) => patchSettings({ daily_report_email: e.target.value.trim() || null })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              data-testid="daily-report-email-input"
            />
          </div>
          <div>
            <label className="block text-xs text-semantic-muted mb-1">Heure d'envoi (local)</label>
            <select
              value={settings.daily_report_hour ?? 18}
              onChange={(e) => patchSettings({ daily_report_hour: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              data-testid="daily-report-hour-select"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-semantic-muted mb-1">Minute</label>
            <select
              value={settings.daily_report_minute ?? 0}
              onChange={(e) => patchSettings({ daily_report_minute: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              data-testid="daily-report-minute-select"
            >
              {[0, 15, 30, 45].map((m) => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-semantic-muted" data-testid="daily-report-last-sent">
            {settings.last_report_sent_at ? (
              <>
                Dernier envoi :{' '}
                <span className="font-medium text-semantic">
                  {formatDateTime(settings.last_report_sent_at)}
                </span>
                {' — '}
                <span
                  className={
                    settings.last_report_status === 'sent'
                      ? 'text-green-600 font-medium'
                      : 'text-red-600 font-medium'
                  }
                >
                  {settings.last_report_status === 'sent' ? '✓ envoyé' : '✗ échec'}
                </span>
                {settings.last_report_error && (
                  <span className="ml-1">({settings.last_report_error})</span>
                )}
              </>
            ) : (
              <>Aucun rapport envoyé pour le moment.</>
            )}
          </div>
          <PillButton
            variant="outline"
            onClick={handleSendReportTest}
            disabled={sendingReportTest}
            data-testid="send-report-test-btn"
          >
            {sendingReportTest ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Envoi en cours…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Envoyer un rapport test maintenant
              </>
            )}
          </PillButton>
        </div>

        {reportResult && (
          <div
            className="mt-4 p-3 rounded-xl text-sm"
            style={{
              backgroundColor: reportResult.success ? 'hsl(142, 70%, 95%)' : 'hsl(0, 70%, 95%)',
              color: reportResult.success ? 'hsl(142, 70%, 30%)' : 'hsl(0, 70%, 35%)',
            }}
            data-testid="daily-report-result"
          >
            {reportResult.success ? (
              <>
                ✓ Rapport envoyé à <strong>{reportResult.recipient}</strong>
                {reportResult.email_id && (
                  <span className="opacity-70"> (id {reportResult.email_id})</span>
                )}
              </>
            ) : (
              <>
                ✗ Échec de l'envoi
                {reportResult.error && <> — code <code>{reportResult.error}</code></>}
                {reportResult.message && <> — {reportResult.message}</>}
              </>
            )}
          </div>
        )}

        <div className="mt-4 text-xs text-semantic-muted flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            <strong>Architecture :</strong> Resend est utilisé pour ce rapport interne. Brevo ne
            reçoit jamais ce type d'email — il sert uniquement à synchroniser les contacts
            prospects qualifiés (CRM).
          </span>
        </div>
      </GlassCard>

      {/* P0 iter 119: Bulk sync confirmation modal */}
      {bulkSyncConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setBulkSyncConfirm(false)}
          data-testid="bulk-sync-confirm-modal"
        >
          <div
            className="w-full max-w-lg rounded-2xl p-6"
            style={{ backgroundColor: 'hsl(var(--color-surface))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-semantic mb-2">
              Confirmer la synchronisation Brevo
            </h3>
            <p className="text-sm text-semantic-muted mb-3">
              Vous allez synchroniser <strong>{brevoDiag?.qualified_syncable_count || 0}</strong>{' '}
              contact(s) qualifié(s) avec emails B2B valides vers Brevo.
            </p>
            {settings?.dry_run && (
              <div
                className="p-3 rounded-xl text-sm mb-3"
                style={{ backgroundColor: 'hsl(40, 100%, 95%)', color: 'hsl(40, 100%, 30%)' }}
              >
                ⚠️ Mode simulation actif. Cochez « Forcer LIVE » ci-dessous pour pousser réellement
                ces contacts (requis car votre paramètre dry-run est encore actif).
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <PillButton
                variant="outline"
                className="flex-1"
                onClick={() => setBulkSyncConfirm(false)}
                data-testid="bulk-sync-cancel-btn"
              >
                Annuler
              </PillButton>
              {settings?.dry_run ? (
                <PillButton
                  variant="primary"
                  className="flex-1"
                  onClick={() => handleBulkSync(true)}
                  disabled={bulkSyncing}
                  data-testid="bulk-sync-force-live-btn"
                >
                  Forcer LIVE & synchroniser
                </PillButton>
              ) : (
                <PillButton
                  variant="primary"
                  className="flex-1"
                  onClick={() => handleBulkSync(false)}
                  disabled={bulkSyncing}
                  data-testid="bulk-sync-confirm-btn"
                >
                  Confirmer & synchroniser
                </PillButton>
              )}
            </div>
          </div>
        </div>
      )}

      {/* === Activation card === */}
      <GlassCard className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'hsl(142, 70%, 90%)', color: 'hsl(142, 70%, 35%)' }}
            >
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-semantic">Mode autonome</h2>
              <p className="text-sm text-semantic-muted">
                L'agent utilise les données de votre Site Scanner pour rechercher des
                prospects automatiquement chaque jour.
              </p>
            </div>
          </div>
          <button
            onClick={() => patchSettings({ enabled: !settings.enabled })}
            disabled={saving || !settings.project_id}
            className="relative w-14 h-7 rounded-full transition-colors disabled:opacity-40"
            style={{
              backgroundColor: settings.enabled ? 'hsl(142, 70%, 45%)' : 'hsl(220, 10%, 70%)',
            }}
            data-testid="autonomous-enabled-toggle"
            title={settings.enabled ? 'Désactiver' : 'Activer'}
          >
            <span
              className="absolute top-0.5 w-6 h-6 rounded-full bg-white transition-transform shadow"
              style={{ transform: settings.enabled ? 'translateX(28px)' : 'translateX(2px)' }}
            />
          </button>
        </div>

        {/* Project source selector */}
        {!hasProjects ? (
          <div
            className="p-4 rounded-xl flex items-start gap-3"
            style={{ backgroundColor: 'hsl(40, 100%, 95%)', color: 'hsl(40, 100%, 30%)' }}
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">Aucun projet analysé</p>
              <p className="text-xs">
                Lancez d'abord le Site Scanner sur un projet (étape "Analyser mon site")
                pour pouvoir activer l'agent autonome.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Projet source (Site Scanner)
              </label>
              <select
                value={settings.project_id || ''}
                onChange={(e) => patchSettings({ project_id: e.target.value || null })}
                disabled={saving}
                className="w-full px-3 py-2 rounded-lg text-semantic"
                style={{
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border-subtle))',
                }}
                data-testid="autonomous-project-select"
              >
                <option value="">— Choisir un projet —</option>
                {scannedProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.language ? `(${p.language})` : ''}
                  </option>
                ))}
              </select>
              {settings.project_id && (
                <div className="mt-2 text-xs text-semantic-muted">
                  {(() => {
                    const p = scannedProjects.find((x) => x.id === settings.project_id);
                    if (!p) return null;
                    return (
                      <span>
                        Cible: {p.target_audience || '—'} • Thèmes: {(p.themes || []).join(', ') || '—'}
                      </span>
                    );
                  })()}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Fuseau horaire
              </label>
              <select
                value={settings.timezone}
                onChange={(e) => patchSettings({ timezone: e.target.value })}
                disabled={saving}
                className="w-full px-3 py-2 rounded-lg text-semantic"
                style={{
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border-subtle))',
                }}
                data-testid="autonomous-timezone-select"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Heure d'exécution quotidienne
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={settings.run_hour}
                  onChange={(e) => patchSettings({ run_hour: Number(e.target.value) })}
                  disabled={saving}
                  className="w-20 px-3 py-2 rounded-lg text-semantic"
                  style={{
                    backgroundColor: 'hsl(var(--color-surface-2))',
                    border: '1px solid hsl(var(--color-border-subtle))',
                  }}
                  data-testid="autonomous-run-hour"
                />
                <span className="text-semantic">:</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={settings.run_minute}
                  onChange={(e) => patchSettings({ run_minute: Number(e.target.value) })}
                  disabled={saving}
                  className="w-20 px-3 py-2 rounded-lg text-semantic"
                  style={{
                    backgroundColor: 'hsl(var(--color-surface-2))',
                    border: '1px solid hsl(var(--color-border-subtle))',
                  }}
                  data-testid="autonomous-run-minute"
                />
                <span className="text-xs text-semantic-muted ml-2">
                  ({settings.timezone})
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Objectif contacts qualifiés / jour
              </label>
              <input
                type="number"
                min={1}
                max={1000}
                value={settings.daily_target_qualified_contacts}
                onChange={(e) => patchSettings({ daily_target_qualified_contacts: Number(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 rounded-lg text-semantic"
                style={{
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border-subtle))',
                }}
                data-testid="autonomous-daily-target"
              />
              <div className="text-xs text-semantic-muted mt-1">
                L'agent boucle jusqu'à atteindre ce nombre.
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Cap technique prospects bruts / jour
              </label>
              <input
                type="number"
                min={10}
                max={5000}
                value={settings.max_raw_prospects_per_day}
                onChange={(e) => patchSettings({ max_raw_prospects_per_day: Number(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 rounded-lg text-semantic"
                style={{
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border-subtle))',
                }}
                data-testid="autonomous-raw-cap"
              />
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Cycles max par run
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={settings.max_cycles_per_run}
                onChange={(e) => patchSettings({ max_cycles_per_run: Number(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 rounded-lg text-semantic"
                style={{
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border-subtle))',
                }}
                data-testid="autonomous-max-cycles"
              />
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Limite quotidienne (nouveaux prospects)
              </label>
              <input
                type="number"
                min={1}
                max={500}
                value={settings.daily_limit_new_prospects}
                onChange={(e) => patchSettings({ daily_limit_new_prospects: Number(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 rounded-lg text-semantic"
                style={{
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border-subtle))',
                }}
                data-testid="autonomous-daily-limit"
              />
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Seuil de qualification ({settings.qualification_threshold}/100)
              </label>
              <input
                type="range"
                min={0}
                max={100}
                value={settings.qualification_threshold}
                onChange={(e) => patchSettings({ qualification_threshold: Number(e.target.value) })}
                disabled={saving}
                className="w-full"
                data-testid="autonomous-threshold-slider"
              />
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-semantic-muted mb-1.5">
                Requêtes par run / résultats par requête
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={settings.queries_per_run}
                  onChange={(e) => patchSettings({ queries_per_run: Number(e.target.value) })}
                  disabled={saving}
                  className="w-24 px-3 py-2 rounded-lg text-semantic"
                  style={{
                    backgroundColor: 'hsl(var(--color-surface-2))',
                    border: '1px solid hsl(var(--color-border-subtle))',
                  }}
                  data-testid="autonomous-queries-per-run"
                />
                <span className="text-xs text-semantic-muted">×</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={settings.results_per_query}
                  onChange={(e) => patchSettings({ results_per_query: Number(e.target.value) })}
                  disabled={saving}
                  className="w-24 px-3 py-2 rounded-lg text-semantic"
                  style={{
                    backgroundColor: 'hsl(var(--color-surface-2))',
                    border: '1px solid hsl(var(--color-border-subtle))',
                  }}
                  data-testid="autonomous-results-per-query"
                />
                <span className="text-xs text-semantic-muted">résultats</span>
              </div>
            </div>
          </div>
        )}

        {/* Mode + Brevo controls */}
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div
            className="p-3 rounded-xl flex items-center gap-3"
            style={{
              backgroundColor: settings.dry_run ? 'hsl(40, 100%, 95%)' : 'hsl(0, 70%, 95%)',
              border: `2px solid ${settings.dry_run ? 'hsl(40, 100%, 50%)' : 'hsl(0, 70%, 50%)'}`,
            }}
          >
            <div className="flex-1">
              <div
                className="text-xs font-bold uppercase tracking-wide"
                style={{ color: settings.dry_run ? 'hsl(40, 100%, 35%)' : 'hsl(0, 70%, 40%)' }}
              >
                {settings.dry_run ? 'Mode Simulation' : 'Mode Live'}
              </div>
              <div className="text-xs text-semantic-muted">
                {settings.dry_run
                  ? "Aucune action sur Brevo (sécurité)"
                  : 'Sync RÉELLE Brevo si auto-sync activé'}
              </div>
            </div>
            <button
              onClick={() => patchSettings({ dry_run: !settings.dry_run })}
              disabled={saving}
              className="relative w-12 h-6 rounded-full transition-colors"
              style={{ backgroundColor: settings.dry_run ? 'hsl(40, 100%, 50%)' : 'hsl(0, 70%, 50%)' }}
              data-testid="autonomous-dry-run-toggle"
            >
              <span
                className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform"
                style={{ transform: settings.dry_run ? 'translateX(2px)' : 'translateX(26px)' }}
              />
            </button>
          </div>

          <div
            className="p-3 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
          >
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-wide text-semantic">
                Auto-sync Brevo
              </div>
              <div className="text-xs text-semantic-muted">
                Pousse les contacts qualifiés vers Brevo (live uniquement)
              </div>
            </div>
            <button
              onClick={() => patchSettings({ auto_sync_brevo: !settings.auto_sync_brevo })}
              disabled={saving}
              className="relative w-12 h-6 rounded-full transition-colors"
              style={{ backgroundColor: settings.auto_sync_brevo ? 'hsl(142, 70%, 45%)' : 'hsl(220, 10%, 70%)' }}
              data-testid="autonomous-auto-sync-toggle"
            >
              <span
                className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform"
                style={{ transform: settings.auto_sync_brevo ? 'translateX(26px)' : 'translateX(2px)' }}
              />
            </button>
          </div>
        </div>

        {/* Schedule + actions */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 min-w-0">
            <StatPill icon={Clock} label="Dernière exécution" value={formatDateTime(settings.last_run_at)} color="hsl(220, 60%, 50%)" />
            <StatPill icon={Calendar} label="Prochaine exécution" value={settings.enabled ? formatDateTime(settings.next_run_at) : 'Désactivé'} color="hsl(142, 70%, 45%)" />
            <StatPill icon={Target} label="Statut" value={settings.enabled ? 'Actif' : 'Inactif'} color={settings.enabled ? 'hsl(142, 70%, 45%)' : 'hsl(220, 10%, 60%)'} />
            <StatPill icon={Activity} label="Mode" value={settings.dry_run ? 'Simulation' : 'Live'} color={settings.dry_run ? 'hsl(40, 100%, 50%)' : 'hsl(0, 70%, 50%)'} />
          </div>

          <div className="flex items-center gap-2">
            <PillButton
              variant="primary"
              onClick={handleRunNow}
              disabled={running || !settings.project_id}
              data-testid="autonomous-run-now-btn"
            >
              {running ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Exécution…
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Lancer maintenant
                </>
              )}
            </PillButton>
            <PillButton
              variant="ghost"
              onClick={handleStop}
              disabled={stopping}
              data-testid="autonomous-stop-btn"
              title="Arrêt immédiat (STOP)"
            >
              <StopCircle className="w-4 h-4 text-red-500" />
              STOP
            </PillButton>
          </div>
        </div>
      </GlassCard>

      {/* === Generated queries preview === */}
      <GlassCard className="p-6" data-testid="autonomous-queries-card">
        <h3 className="text-base font-semibold text-semantic mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-semantic-muted" />
          Requêtes générées automatiquement
        </h3>
        {generatedQueries.length === 0 ? (
          <p className="text-sm text-semantic-muted">
            Aucune requête encore générée. Lancez un run pour voir les requêtes ciblées
            que l'agent a créées à partir du Site Scanner.
          </p>
        ) : (
          <ul className="space-y-2">
            {generatedQueries.map((q, idx) => (
              <li
                key={idx}
                className="p-3 rounded-xl flex items-start gap-3"
                style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              >
                <Globe className="w-4 h-4 text-semantic-muted flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-semantic text-sm">{q.query}</div>
                  {q.rationale && (
                    <div className="text-xs text-semantic-muted mt-0.5">{q.rationale}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      {/* === History === */}
      <GlassCard className="p-6" data-testid="autonomous-history-card">
        <h3 className="text-base font-semibold text-semantic mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-semantic-muted" />
          Historique des runs
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-semantic-muted">Aucun run pour le moment.</p>
        ) : (
          <div className="space-y-2">
            {history.map((run) => {
              const stats = run.stats || {};
              const ok = run.status === 'completed';
              const partial = run.status === 'completed_with_errors';
              const fail = run.status === 'failed';
              const Icon = ok ? CheckCircle : fail ? XCircle : AlertCircle;
              const color = ok
                ? 'hsl(142, 70%, 45%)'
                : fail
                ? 'hsl(0, 70%, 50%)'
                : 'hsl(40, 100%, 50%)';

              // Aggregate error codes for explicit display
              const errors = run.errors || [];
              const errorCodes = {};
              errors.forEach((e) => {
                const code = e.code || e.stage || 'unknown';
                errorCodes[code] = (errorCodes[code] || 0) + 1;
              });

              return (
                <div
                  key={run.run_id}
                  className="p-3 rounded-xl"
                  style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                >
                  <div className="flex items-center gap-4">
                    <Icon className="w-5 h-5 flex-shrink-0" style={{ color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-semantic">
                        {formatDateTime(run.started_at)} • {run.triggered_by}
                        {run.source === 'local_enrichment' && (
                          <span
                            className="ml-2 text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: 'hsl(200, 70%, 92%)', color: 'hsl(200, 70%, 30%)' }}
                            data-testid="run-source-local-enrichment"
                            title={run.reason || 'local_enrichment'}
                          >
                            enrichissement local
                          </span>
                        )}
                        <span
                          className="ml-2 text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: color + '20', color }}
                        >
                          {run.status}
                        </span>
                        {run.dry_run && (
                          <span
                            className="ml-1 text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: 'hsl(40, 100%, 90%)', color: 'hsl(40, 100%, 30%)' }}
                          >
                            simulation
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-semantic-muted">
                        {run.source === 'local_enrichment' ? (
                          <>
                            {stats.prospects_scanned || 0} prospects scannés •{' '}
                            {stats.enriched || 0} enrichis • {stats.qualified || 0} qualifiés •{' '}
                            {stats.synced_to_brevo || 0} sync Brevo • {stats.errors || 0} erreurs
                          </>
                        ) : (
                          <>
                            {stats.queries_executed || 0} requêtes • {stats.new_prospects || 0} nouveaux •{' '}
                            {stats.enriched || 0} enrichis • {stats.qualified || 0} qualifiés • {stats.synced_to_brevo || 0} sync Brevo •{' '}
                            {stats.errors || 0} erreurs
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-semantic-muted">{run.duration_seconds || 0}s</div>
                  </div>
                  {Object.keys(errorCodes).length > 0 && (
                    <div className="mt-2 pl-9 flex flex-wrap gap-1.5" data-testid="run-error-codes">
                      {Object.entries(errorCodes).map(([code, count]) => (
                        <span
                          key={code}
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: 'hsl(0, 70%, 95%)', color: 'hsl(0, 70%, 35%)' }}
                          title={`Erreur ${code}`}
                        >
                          {code} × {count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>
    </div>
  );
}
